// Google Drive API Service
// Handles OAuth, file uploads, and folder organization for receipts

const CLIENT_ID = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID;
const SCOPES = 'https://www.googleapis.com/auth/drive.file';

let tokenClient = null;
let accessToken = null;

// ============================================================
// OAUTH INITIALIZATION
// ============================================================
export function initGoogleDrive() {
  return new Promise((resolve) => {
    // Wait for the Google Identity Services script to load
    const checkGsi = setInterval(() => {
      if (window.google?.accounts?.oauth2) {
        clearInterval(checkGsi);
        tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPES,
          callback: (response) => {
            if (response.access_token) {
              accessToken = response.access_token;
            }
          },
        });
        resolve(true);
      }
    }, 100);
    // Timeout after 10 seconds
    setTimeout(() => { clearInterval(checkGsi); resolve(false); }, 10000);
  });
}

export function requestDriveAccess() {
  return new Promise((resolve, reject) => {
    if (!tokenClient) {
      reject(new Error('Google Drive not initialized'));
      return;
    }
    tokenClient.callback = (response) => {
      if (response.error) {
        reject(new Error(response.error));
        return;
      }
      accessToken = response.access_token;
      resolve(accessToken);
    };
    tokenClient.requestAccessToken();
  });
}

export function hasDriveAccess() {
  return !!accessToken;
}

// ============================================================
// FOLDER MANAGEMENT
// ============================================================
const folderCache = {};

async function findOrCreateFolder(name, parentId = null) {
  const cacheKey = `${parentId || 'root'}/${name}`;
  if (folderCache[cacheKey]) return folderCache[cacheKey];

  // Search for existing folder
  let q = `name='${name}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
  if (parentId) q += ` and '${parentId}' in parents`;

  const searchRes = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id,name)`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  const searchData = await searchRes.json();

  if (searchData.files && searchData.files.length > 0) {
    folderCache[cacheKey] = searchData.files[0].id;
    return searchData.files[0].id;
  }

  // Create folder
  const metadata = {
    name,
    mimeType: 'application/vnd.google-apps.folder',
    ...(parentId && { parents: [parentId] }),
  };
  const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(metadata),
  });
  const folder = await createRes.json();
  folderCache[cacheKey] = folder.id;
  return folder.id;
}

// Build the full folder path: /Satellite Gardens/Receipts/YYYY/MM-Month/JobName/
async function getReceiptFolderId(date, jobName) {
  const d = new Date(date + 'T00:00:00');
  const year = d.getFullYear().toString();
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthStr = `${String(d.getMonth() + 1).padStart(2, '0')}-${monthNames[d.getMonth()]}`;
  const folderName = jobName ? jobName.toLowerCase().replace(/\s+/g, '-') : 'overhead';

  const sgFolder = await findOrCreateFolder('Satellite Gardens');
  const receiptsFolder = await findOrCreateFolder('Receipts', sgFolder);
  const yearFolder = await findOrCreateFolder(year, receiptsFolder);
  const monthFolder = await findOrCreateFolder(monthStr, yearFolder);
  const jobFolder = await findOrCreateFolder(folderName, monthFolder);
  return jobFolder;
}

// ============================================================
// FILE UPLOAD
// ============================================================
export async function uploadReceiptFile(file, receiptMeta) {
  if (!accessToken) {
    await requestDriveAccess();
  }

  const { date, vendor, description, jobName } = receiptMeta;

  // Build filename: vendor_date_description.ext
  const ext = file.name.split('.').pop().toLowerCase();
  const safeVendor = (vendor || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '');
  const safeDesc = (description || 'receipt').toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 30);
  const fileName = `${safeVendor}_${date}_${safeDesc}.${ext}`;

  // Get the target folder
  const folderId = await getReceiptFolderId(date, jobName);

  // Upload using multipart
  const metadata = {
    name: fileName,
    parents: [folderId],
  };

  const formData = new FormData();
  formData.append(
    'metadata',
    new Blob([JSON.stringify(metadata)], { type: 'application/json' })
  );
  formData.append('file', file);

  const response = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink,webContentLink',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    }
  );

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error?.message || 'Upload failed');
  }

  const driveFile = await response.json();
  return {
    fileId: driveFile.id,
    webViewLink: driveFile.webViewLink,
    webContentLink: driveFile.webContentLink,
  };
}

// ============================================================
// FILE READING (for AI extraction)
// ============================================================
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:image/jpeg;base64,xxxxx"
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function getMediaType(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  const map = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    pdf: 'application/pdf',
  };
  return map[ext] || 'application/octet-stream';
}
